import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Row, Col, Button } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import PageHeader1 from '../../components/common/PageHeader1';
import apiClient from "../../utils/api";
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';

const PARKING_TYPES = [
    { value: 'Normal', label: 'Normal' },
    { value: 'VIP', label: 'VIP' },
    { value: 'Complementary', label: 'Complementary' }
];

function ParkingAdd() {    
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(false);
    
    // Check if user should see restricted fields
    const shouldHideFields = user?.roles?.includes('Driver') || user?.roles?.includes('Supervisor');
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [colors, setColors] = useState([]);
    const [exitLocations, setExitLocations] = useState([]);
    const [damageMarks, setDamageMarks] = useState([]);
    const [hasDamage, setHasDamage] = useState(false);
    const [drivers, setDrivers] = useState([]);
    const [formData, setFormData] = useState({
        ticket_number: '',
        mobile_number: '',
        plate_number: '',
        brand_id: '',
        model_id: '',
        color_id: '',
        images: [],
        notes: '',
        parking_time: moment().format('HH:mm'),
        parking_date: moment().format('YYYY-MM-DD'),
        parking_type: 'Normal', // Default value
        exit_location_id: '',
        driver_id: ''
    });    // Add new state for the edited image
    const [damagedCarImage, setDamagedCarImage] = useState(null);    
    
    useEffect(() => {
        // Load initial data
        fetchColors();
        fetchBrands();
        fetchNextTicketNumber();
        fetchExitLocations(formData.parking_type); // Pass initial parking type

        // If an exit location is already selected, fetch drivers for that location
        if (formData.exit_location_id) {
            fetchDrivers(formData.exit_location_id);
        }
    }, []);
        const fetchExitLocations = async (parkingType) => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/parkings/active-exit-locations/${parkingType}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const locations = response.data.data;
            setExitLocations(locations);

            // If only one location is available, auto-select it
            if (locations.length === 1) {
                const locationId = locations[0].id.toString();
                setFormData(prev => ({
                    ...prev,
                    exit_location_id: locationId
                }));
                
                // Also fetch drivers for this location
                fetchDrivers(locationId);
            } else {
                // Clear Valet Location if multiple options are available
                setFormData(prev => ({
                    ...prev,
                    exit_location_id: ''
                }));
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load Valet Locations');
        }
    };

    const fetchNextTicketNumber = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get('accounts/parkings/next-ticket-number', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormData(prev => ({
                ...prev,
                ticket_number: response.data.ticket_number
            }));
        } catch (error) {
            console.error('Error fetching next ticket number:', error);
            toast.error('Error loading ticket number');
        }
    };

    // Add this new function to fetch colors
    const fetchColors = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get('accounts/colors',{
                headers: { Authorization: `Bearer ${token}` }
            });
            // Filter only active colors
            const activeColors = response.data.data.filter(color => color.is_active === 1);
            setColors(activeColors);
        } catch (error) {
            console.error('Error fetching colors:', error);
            toast.error('Error loading colors');
            // Fallback to empty array if API fails
            setColors([]);
        }
    };

    // Add this new function to fetch brands
    const fetchBrands = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get('accounts/brands',{
                headers: { Authorization: `Bearer ${token}` }
            });
            // Filter only active brands
            const activeBrands = response.data.data.filter(brand => brand.is_active === 1);
            setBrands(activeBrands);
        } catch (error) {
            console.error('Error fetching brands:', error);
            toast.error('Error loading brands');
            setBrands([]);
        }
    };

    const fetchModels = async (brandId) => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get('accounts/models', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Filter models by brand and active status
            const allModels = response.data.data.filter(model => 
                model.brand_id === parseInt(brandId) && 
                model.is_active === 1
            );
            setModels(allModels);
        } catch (error) {
            console.error('Error fetching models:', error);
            toast.error('Error loading car models');
            setModels([]);
        }
    };

    const fetchDrivers = async (locationId) => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/drivers/by-location/${locationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const driversData = response.data.data;
            setDrivers(driversData);
            
            // Auto-select driver logic
            if (driversData.length === 1) {
                // If only one driver, auto-select it
                setFormData(prev => ({
                    ...prev,
                    driver_id: driversData[0].id.toString()
                }));
            } else if (driversData.length > 1 && user) {
                // If multiple drivers, try to auto-select the logged-in user if they're in the list
                const currentUserDriver = driversData.find(driver => 
                    driver.user_id === user.id || 
                    driver.email === user.email ||
                    driver.id === user.id
                );
                
                if (currentUserDriver) {
                    setFormData(prev => ({
                        ...prev,
                        driver_id: currentUserDriver.id.toString()
                    }));
                } else {
                    // Clear driver selection if current user is not in the list
                    setFormData(prev => ({
                        ...prev,
                        driver_id: ''
                    }));
                }
            } else {
                // Clear driver selection when no drivers or location changes
                setFormData(prev => ({
                    ...prev,
                    driver_id: ''
                }));
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load drivers');
            setDrivers([]);
            setFormData(prev => ({
                ...prev,
                driver_id: ''
            }));
        }
    };

    // New function to fetch vehicle info by plate number
    const fetchVehicleByPlateNumber = async (plateNumber) => {
        if (!plateNumber || plateNumber.length < 2) return; // Don't fetch for very short inputs
        
        try {
            setIsLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.post('accounts/parkings/find-by-plate', 
                { plate_number: plateNumber },
                { headers: { Authorization: `Bearer ${token}` }}
            );
            
            if (response.data.data) {
                const vehicleData = response.data.data;
                
                // If the brand exists in the current brands list, select it and fetch models
                if (vehicleData.brand_id) {
                    const brandExists = brands.some(b => b.id === vehicleData.brand_id);
                    if (brandExists) {
                        await fetchModels(vehicleData.brand_id.toString());
                    }
                }
                
                // Update form with vehicle details
                setFormData(prev => ({
                    ...prev,                    
                    brand_id: vehicleData.brand_id ? vehicleData.brand_id.toString() : '',
                    model_id: vehicleData.model_id ? vehicleData.model_id.toString() : '',
                    color_id: vehicleData.color_id ? vehicleData.color_id.toString() : '',
                    mobile_number: vehicleData.mobile_number || '',
                    notes: vehicleData.notes || ''
                }));
                
                toast.info("Previous vehicle info loaded");
            }
        } catch (error) {
            // If 404, it means no vehicle found - this is normal behavior
            if (error.response?.status !== 404) {
                console.error('Error fetching vehicle data:', error);
            }
        } finally {
            setIsLoading(false);
        }
    };    // Create a debounced version of the fetch function to avoid excessive API calls
    const debouncedFetchVehicle = useCallback(
        debounce((plateNumber) => {
            fetchVehicleByPlateNumber(plateNumber);
        }, 500), // Wait for 500ms of inactivity before making the API call
    [brands]);  // Re-create if brands change

    const handleInputChange = (e) => {
        const { name, value, type, files } = e.target;

        if (name === 'mobile_number') {
            // Only allow digits and limit to 8 characters
            const sanitizedValue = value.replace(/\D/g, '').slice(0, 8);
            setFormData(prev => ({
                ...prev,
                [name]: sanitizedValue
            }));
            return;
        }

        if (type === 'file') {
            setFormData(prev => ({
                ...prev,
                [name]: Array.from(files)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }       
        
        // Fetch vehicle data when plate number is entered
        if (name === 'plate_number' && value) {
            // Use a debounce approach to avoid too many API calls while typing
            if (value.length >= 3) { // Only fetch if plate number has at least 3 characters
                debouncedFetchVehicle(value);
            }
        }
        
        // Fetch Valet Locations when parking type changes
        if (name === 'parking_type') {
            fetchExitLocations(value);
        }
        
        // Fetch drivers when Valet Location changes
        if (name === 'exit_location_id' && value) {
            fetchDrivers(value);
        }
        
        // Fetch car models when brand changes
        if (name === 'brand_id' && value) {
            fetchModels(value);
            setFormData(prev => ({
                ...prev,
                model_id: ''
            }));
        }

        if (name === 'exit_location_id' && value) {
            fetchDrivers(value); // Fetch drivers when Valet Location changes
        }

        // Fetch vehicle details when plate number changes
        if (name === 'plate_number' && value) {
            fetchVehicleByPlateNumber(value);
        }
    };

    const validateMobileNumber = (number) => /^\d{8}$/.test(number);

    const handleSubmit = async (e) => {
        e.preventDefault();        
        
        if (formData.mobile_number && formData.mobile_number.length > 0 && !validateMobileNumber(formData.mobile_number)) {
            toast.error('If provided, mobile number must be exactly 8 digits');
            return;
        }

        setIsLoading(true);
        try {
            const data = new FormData();
            
            // Required fields from API validation
            data.append('ticket_number', formData.ticket_number);
            data.append('plate_number', formData.plate_number);
            
            // Optional fields
            if (formData.mobile_number) {
                data.append('mobile_number', formData.mobile_number);
            }
            if (formData.brand_id) {
                data.append('brand_id', formData.brand_id);
            }
            if (formData.model_id) {
                data.append('model_id', formData.model_id);
            }
            if (formData.color_id) {
                data.append('color_id', formData.color_id);
            }            
            if (formData.notes) {
                data.append('notes', formData.notes);
            }
            if (formData.exit_location_id) {
                data.append('exit_location_id', formData.exit_location_id);
            }
            if (formData.driver_id) {
                data.append('driver_id', formData.driver_id);
            }

            // Handle damage marks
            data.append('has_damage', hasDamage ? 1 : 0);
            data.append('damage_marks', JSON.stringify(damageMarks));

            // Handle damage image if exists
            if (damagedCarImage) {
                data.append('damage_image', damagedCarImage, 'damage_image.png');
            }

            // Required parking date/time            
            data.append('parking_time', moment(formData.parking_time, 'HH:mm').format('HH:mm:ss'));
            data.append('parking_date', moment(formData.parking_date).format('YYYY-MM-DD'));
            data.append('parking_type', formData.parking_type);

            // Handle multiple images
            if (formData.images.length > 0) {
                formData.images.forEach(image => {
                    data.append('images[]', image);
                });
            }

            const token = localStorage.getItem('auth_token');
            const response = await apiClient.post('accounts/parkings', data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            toast.success('Parking ticket created successfully');
            
            // Redirect based on whether mobile number was provided
            if (!formData.mobile_number) {
                // If no mobile number, go to print options
                navigate(`/accounts/parking-print-options/${response.data.data.id}`);
            } else {
                // If mobile number was provided, go directly to parking list
                navigate('/accounts/parking-list');
            }
        } catch (error) {
            console.error('Error:', error);
            const errorMessage = error.response?.data?.message || 'Error creating parking ticket';
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Modify handleImageClick to capture the edited image
    const handleImageClick = (event) => {
        const isTouch = event.type === 'touchend';
        const touch = isTouch ? event.changedTouches?.[0] : event;
        if (!touch) return;

        const rect = event.target.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        const clickPosition = { x: Math.round(x), y: Math.round(y) };

        setDamageMarks(prev => {
            const newMarks = [...prev, clickPosition];
            setHasDamage(true);
            
            // Create and save the edited image
            const canvas = document.createElement('canvas');
            const img = event.target;
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            
            // Draw original image
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            // Draw all damage marks
            [...prev, clickPosition].forEach(mark => {
                ctx.beginPath();
                ctx.arc(mark.x, mark.y, 5, 0, 2 * Math.PI);
                ctx.fillStyle = 'black';
                ctx.fill();
            });

            // Convert to blob and store
            canvas.toBlob((blob) => {
                setDamagedCarImage(blob);
            }, 'image/png');

            return newMarks;
        });
    };

    const handleRemoveLastMark = () => {
        setDamageMarks(prev => {
            const updated = [...prev];
            updated.pop();
            if (updated.length === 0) {
                setHasDamage(false);
                setDamagedCarImage(null); // Clear damage image when no marks
            }
            return updated;
        });
    };

    const handleResetAllMarks = () => {
        setDamageMarks([]);
        setHasDamage(false);
        setDamagedCarImage(null); // Clear damage image when resetting
    };

    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? '#a7824f' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(167, 130, 79, 0.25)' : '',
            '&:hover': {
                borderColor: '#a7824f',
            },
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#a7824f' : state.isFocused ? 'rgba(167, 130, 79, 0.1)' : '',
            color: state.isSelected ? 'white' : 'black',
            '&:active': {
                backgroundColor: '#a7824f',
            },
        }),
    };

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                <PageHeader1 pagetitle="Issue New Ticket" />
                <Card>
                    <Card.Body>
                        <Form onSubmit={handleSubmit}>
                            <Row className="g-3 mb-4">                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Plate Number*</Form.Label>
                                        <div className="position-relative">
                                            <Form.Control
                                                type="text"
                                                name="plate_number"
                                                value={formData.plate_number}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Enter plate number"
                                            />
                                            {isLoading && <small className="text-muted mt-1 d-block">Looking up vehicle information...</small>}
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Mobile Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="mobile_number"
                                            value={formData.mobile_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter 8 digits"
                                            maxLength="8"
                                        />
                                        <Form.Text className="text-muted">
                                            Enter exactly 8 digits (without country code)
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div style={{ background: '#8080800f', padding: '20px', borderRadius: '15px' }} className="mb-4">
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Parking Type</Form.Label>
                                            <Select
                                                name="parking_type"
                                                value={PARKING_TYPES.find(type => type.value === formData.parking_type)}
                                                onChange={(selectedOption) => {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'parking_type',
                                                            value: selectedOption ? selectedOption.value : ''
                                                        }
                                                    });
                                                }}
                                                options={PARKING_TYPES}
                                                getOptionLabel={(option) => option.label}
                                                getOptionValue={(option) => option.value}
                                                isClearable={false}
                                                isSearchable
                                                placeholder="Select parking type"
                                                noOptionsMessage={() => PARKING_TYPES.length === 0 ? "Loading types..." : "No types found"}
                                                styles={customStyles}
                                                isLoading={PARKING_TYPES.length === 0}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    {/* {!shouldHideFields && ( */}
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Valet Location</Form.Label>
                                                <Select
                                                    key={`exit-location-${exitLocations.length}`}
                                                    name="exit_location_id"
                                                    value={exitLocations.find(location => location.id === parseInt(formData.exit_location_id)) || null}
                                                    onChange={(selectedOption) => {
                                                        handleInputChange({
                                                            target: {
                                                                name: 'exit_location_id',
                                                                value: selectedOption ? selectedOption.id.toString() : ''
                                                            }
                                                        });
                                                    }}
                                                    options={exitLocations}
                                                    getOptionLabel={(option) => option.name}
                                                    getOptionValue={(option) => option.id.toString()}
                                                    isClearable={false}
                                                    isSearchable
                                                    placeholder="Search or select location"
                                                    noOptionsMessage={() => exitLocations.length === 0 ? "Loading locations..." : "No locations found"}
                                                    styles={customStyles}
                                                    isLoading={exitLocations.length === 0}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    {/* )} */}
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Driver</Form.Label>
                                            <Select
                                                key={`driver-${drivers.length}-${formData.driver_id}`}
                                                name="driver_id"
                                                value={drivers.find(driver => driver.id === parseInt(formData.driver_id)) || null}
                                                onChange={(selectedOption) => {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'driver_id',
                                                            value: selectedOption ? selectedOption.id.toString() : ''
                                                        }
                                                    });
                                                }}
                                                options={drivers}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id.toString()}
                                                isClearable={false}
                                                isSearchable
                                                placeholder="Search or select driver"
                                                noOptionsMessage={() => !formData.exit_location_id ? "Select a location first" : drivers.length === 0 ? "Loading drivers..." : "No drivers found"}
                                                styles={customStyles}
                                                isDisabled={!formData.exit_location_id}
                                                isLoading={formData.exit_location_id && drivers.length === 0}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    {!shouldHideFields && (
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Ticket Number</Form.Label>                                            <Form.Control
                                                    type="text"
                                                    name="ticket_number"
                                                    value={formData.ticket_number}
                                                    readOnly
                                                    placeholder="Generating ticket number..."
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}
                                    {!shouldHideFields && (
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Car Color</Form.Label>
                                                <Select
                                                    key={`color-${colors.length}-${formData.color_id}`}
                                                    name="color_id"
                                                    value={colors.find(color => color.id === parseInt(formData.color_id)) || null}
                                                    onChange={(selectedOption) => {
                                                        handleInputChange({
                                                            target: {
                                                                name: 'color_id',
                                                                value: selectedOption ? selectedOption.id.toString() : ''
                                                            }
                                                        });
                                                    }}
                                                    options={colors}
                                                    getOptionLabel={(option) => option.name}
                                                    getOptionValue={(option) => option.id.toString()}
                                                    isClearable
                                                    isSearchable
                                                    placeholder="Search or select color"
                                                    noOptionsMessage={() => colors.length === 0 ? "Loading colors..." : "No colors found"}
                                                    styles={customStyles}
                                                    isLoading={colors.length === 0}
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}
                                    {!shouldHideFields && (
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Car Brand</Form.Label>
                                                <Select
                                                    key={`brand-${brands.length}-${formData.brand_id}`}
                                                    name="brand_id"
                                                    value={brands.find(brand => brand.id === parseInt(formData.brand_id)) || null}
                                                    onChange={(selectedOption) => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            brand_id: selectedOption ? selectedOption.id.toString() : '',
                                                            model_id: '' // Reset model when brand changes
                                                        }));
                                                        if (selectedOption) {
                                                            fetchModels(selectedOption.id.toString());
                                                        }
                                                    }}
                                                    options={brands}
                                                    getOptionLabel={(option) => option.name}
                                                    getOptionValue={(option) => option.id.toString()}
                                                    isClearable
                                                    isSearchable
                                                    placeholder="Search or select brand"
                                                    noOptionsMessage={() => brands.length === 0 ? "Loading brands..." : "No brands found"}
                                                    styles={customStyles}
                                                    isLoading={brands.length === 0}
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}
                                    {!shouldHideFields && (
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Car Model</Form.Label>
                                                <Select
                                                    key={`model-${models.length}-${formData.model_id}`}
                                                    name="model_id"
                                                    value={models.find(model => model.id === parseInt(formData.model_id)) || null}
                                                    onChange={(selectedOption) => {
                                                        handleInputChange({
                                                            target: {
                                                                name: 'model_id',
                                                                value: selectedOption ? selectedOption.id.toString() : ''
                                                            }
                                                        });
                                                    }}
                                                    options={models}
                                                    getOptionLabel={(option) => option.name}
                                                    getOptionValue={(option) => option.id.toString()}
                                                    isClearable
                                                    isSearchable
                                                    placeholder="Search or select model"
                                                    noOptionsMessage={() => !formData.brand_id ? "Select a brand first" : models.length === 0 ? "Loading models..." : "No models found"}
                                                    styles={customStyles}
                                                    isDisabled={!formData.brand_id}
                                                    isLoading={formData.brand_id && models.length === 0}
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}                                    
                                </Row>
                            </div>
                            <>
                                <style>{`
                                    .custom-outline-red .form-check-input {
                                        border: 2px solid #a7824f;
                                        background-color: white;
                                        transition: all 0.2s ease-in-out;
                                        cursor: pointer;
                                    }

                                    .custom-outline-red .form-check-input:checked {
                                        background-color: #a7824f;
                                        border-color: #a7824f;
                                    }

                                    .custom-outline-red .form-check-input:focus {
                                        box-shadow: none;
                                    }
                                `}</style>
                            </>
                            <div className="mb-4">
                                <h6>Mark Damages</h6>
                                <div className="mb-3 mt-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="There is no damage."
                                        checked={!hasDamage}
                                        onChange={() => {
                                            setHasDamage(false);
                                            setDamageMarks([]);
                                            setDamagedCarImage(null);
                                        }}
                                        className="custom-outline-red"
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        label="There are damages."
                                        checked={hasDamage}
                                        onChange={() => setHasDamage(true)}
                                        className="custom-outline-red"
                                    />
                                </div>
                                <div style={{ display: 'flex', position: 'relative' }}>
                                    
                                    <img
                                        src="/carimage2.png"
                                        alt="Car Diagram"
                                        style={{
                                            width: '100%',
                                            maxWidth: '400px',
                                            borderRadius: '10px',
                                            border: '1px solid #ddd',
                                            cursor: 'crosshair'
                                        }}
                                        onClick={handleImageClick}
                                        onTouchEnd={handleImageClick}
                                    />
                                    {damageMarks.map((dot, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                position: 'absolute',
                                                top: dot.y - 5 + 'px',
                                                left: dot.x - 5 + 'px',
                                                width: '10px',
                                                height: '10px',
                                                backgroundColor: 'black',
                                                borderRadius: '50%'
                                            }}
                                        ></div>
                                    ))}
                                </div>

                                <div className="mt-3 d-flex justify-content-center gap-2">
                                    <Button variant="outline-primary" size="sm" onClick={handleRemoveLastMark}>
                                        Remove Last Mark
                                    </Button>
                                    <Button variant="outline-danger" size="sm" onClick={handleResetAllMarks}>
                                        Reset All Marks
                                    </Button>
                                </div>
                            </div>

                            <div className="mb-4">
                                <Form.Group>
                                    <Form.Label>Upload Images</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="images"
                                        onChange={handleInputChange}
                                        multiple
                                        accept="image/jpeg,image/png"
                                    />
                                    <Form.Text className="text-muted">
                                        You can select multiple images (JPG/PNG)
                                    </Form.Text>
                                </Form.Group>
                            </div>

                            <div style={{ background: '#8080800f', padding: '20px', borderRadius: '15px' }} className="mb-4">
                                <Form.Group>
                                    <Form.Label>Notes</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        placeholder="Enter any additional notes"
                                    />
                                </Form.Group>
                            </div>

                            <div className="mb-4">
                                <Row className="g-3">
                                    {!shouldHideFields && (
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Time of Parking</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    name="parking_time"
                                                    value={formData.parking_time}
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}
                                    {!shouldHideFields && (
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Date of Parking</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="parking_date"
                                                    value={formData.parking_date}
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}
                                </Row>
                            </div>

                            <Button type="submit" variant="primary" disabled={isLoading}>
                                {isLoading ? 'Creating...' : 'Generate Ticket'}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
}

export default ParkingAdd;
